// MA-59 slice 1 — claim-code data access for teacher↔parent linkage.
//
// Backend-only this round: the three RPCs wrapped below are the sole live-DB
// mutation surface for MA-59 (see migration 0024_class_enrollment_claim.sql
// for the full SECURITY DEFINER bodies + ownership checks — a plain client
// UPDATE grant is deliberately absent). No UI calls these yet — teacher
// "reveal code" + parent "enter code" screens are the deferred follow-up
// sub-slice (same MA-59 backlog entry; precedent: MA-155 shipped a pure,
// zero-live-caller primitive the same way).
import { supabase } from "@/supabaseClient.js";
import { captureSupabaseError } from "@/lib/sentry";
import type { SupabaseClient } from "./classApi";

/** PostgREST's code for "function not found in the schema cache" — the
 *  pre-founder-apply state for migration 0024 (mirrors `isMissingTableError`
 *  in classApi.ts for migration 0023). Also matches on the message text as a
 *  fallback: if the exact code is ever wrong for a given PostgREST version,
 *  callers still degrade to the friendly "not live yet" message instead of a
 *  raw error. */
export function isMissingFunctionError(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null | undefined;
  return e?.code === "PGRST202" || /Could not find the function/i.test(e?.message ?? "");
}

interface ClaimedEnrollment {
  id: string;
  class_id: string;
  display_name: string;
  student_id: string;
  claimed_at: string;
}

/** Teacher-only: (re)generates a claim code for one roster row. The RPC
 *  throws if the caller doesn't own the enrollment's class, or if the row
 *  is actively claimed (revoke first — see revokeClaim). */
export async function generateClaimCode(
  enrollmentId: string,
  client: SupabaseClient = supabase,
): Promise<string> {
  const { data, error } = await client.rpc("generate_enrollment_claim_code", {
    p_enrollment_id: enrollmentId,
  });
  if (error) {
    captureSupabaseError("generateClaimCode failed", error, { enrollmentId });
    throw error;
  }
  return data as string;
}

/** Parent-only: redeems a claim code against a student the caller owns. The
 *  RPC throws if the code is invalid/already-claimed/revoked, or the given
 *  student isn't the caller's own. */
export async function claimEnrollment(
  claimCode: string,
  studentId: string,
  client: SupabaseClient = supabase,
): Promise<ClaimedEnrollment> {
  const { data, error } = await client.rpc("claim_class_enrollment", {
    p_claim_code: claimCode,
    p_student_id: studentId,
  });
  if (error) {
    // MA-577 — the SQLSTATE and the RPC's own RAISE message go in the MESSAGE
    // STRING, not only the context object. Before this, a flat console showed
    // `[supabase] claimEnrollment failed: Object Object` and the server's real
    // reason was unreadable without DevTools' Network tab — even though that
    // reason is exactly what distinguishes "you don't own that student" from
    // "code invalid / already claimed / revoked". Same defect class MA-571
    // fixed for recordConsent; this is its second instance.
    captureSupabaseError(
      `claimEnrollment failed (code=${error?.code ?? "none"}): ${error?.message ?? "no message"}`,
      error,
      { studentId, code: error?.code ?? null, details: error?.details ?? null, hint: error?.hint ?? null },
    );
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return row as ClaimedEnrollment;
}

/** Teacher-only: unlinks a claimed enrollment (row + the parent's `students`
 *  row are untouched, only the link is cleared). The RPC throws if the
 *  caller doesn't own the class, or the row isn't currently claimed. */
export async function revokeClaim(
  enrollmentId: string,
  client: SupabaseClient = supabase,
): Promise<void> {
  const { error } = await client.rpc("revoke_class_enrollment_claim", {
    p_enrollment_id: enrollmentId,
  });
  if (error) {
    captureSupabaseError("revokeClaim failed", error, { enrollmentId });
    throw error;
  }
}

// ── MA-292 — self-serve class join, step 1: confirm the class ────────────
export interface ResolvedClass {
  class_name: string;
  grade: string | null;
  teacher_label: string;
}

/** Any signed-in user: resolves a class `join_code` to its NAME + GRADE +
 *  TEACHER LABEL only (migration 0033 — SECURITY DEFINER, authenticated
 *  grant only, NEVER returns the roster or any id; see the migration header
 *  for the privacy guardrail). Returns null when the code doesn't resolve
 *  (unknown, archived, or malformed — indistinguishable by design). Throws
 *  on transport errors; callers use isMissingFunctionError for the
 *  pre-founder-apply friendly message (the MA-59 convention). */
export async function resolveClassByJoinCode(
  joinCode: string,
  client: SupabaseClient = supabase,
): Promise<ResolvedClass | null> {
  const { data, error } = await client.rpc("resolve_class_by_join_code", {
    p_join_code: joinCode,
  });
  if (error) {
    captureSupabaseError("resolveClassByJoinCode failed", error, {});
    throw error;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return row ? (row as ResolvedClass) : null;
}
